import React, { useState } from 'react';
import { supabase } from './supabase/supabaseClient.js';
import { useTranslation } from 'react-i18next'; // 1. Import useTranslation
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaFacebook, FaLine, FaClock, FaCheckCircle, FaPaperPlane } from 'react-icons/fa';
import "./App.css";
import "./contact.css";
import "./Navbar.js";

export default function Contact() {
  const { t } = useTranslation(); // 2. เรียกใช้ Hook แปลภาษา

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase
        .from('contact_messages')
        .insert([formData]);

      if (error) {
        throw error;
      }

      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' }); 
      
      setTimeout(() => {
        setSubmitted(false);
      }, 3000);

    } catch (error) {
      console.error("Error:", error);
      // เปลี่ยน Alert เป็นดึงคำแปลด้วย
      alert(t('contactErrorAlert', 'เกิดข้อผิดพลาดในการส่งข้อความ: ') + error.message);
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    { icon: <FaPhone />, title: t('phoneTitle', 'โทรศัพท์'), details: [{ label: t('mainLineLabel', 'สายหลัก'), value: '090-618-7997', href: 'tel:0906187997' }] },
    { icon: <FaLine />, title: t('lineTitle', 'Line OA'), details: [{ label: '', value: 'ASCENT Medical', href: 'https://line.me/ti/p/~@730aicdq' }] },
    { icon: <FaEnvelope />, title: t('emailTitle', 'อีเมล'), details: [{ label: '', value: 'ascentcorp.info@gmail.com', href: 'mailto:ascentcorp.info@gmail.com' }] },
    { icon: <FaMapMarkerAlt />, title: t('addressTitle', 'ที่อยู่'), details: [{ label: '', value: t('companyAddressFull', 'เลขที่ 214/201 หมู่ที่ 5 ตําบลแม่เหียะ อําเภอเมืองเชียงใหม่ จังหวัดเชียงใหม่ 50100') }] },
    { icon: <FaClock />, title: t('businessHoursTitle', 'เวลาทำการ'), details: [{ label: t('mondayToSaturday', 'จันทร์ - เสาร์'), value: t('businessHoursValue', '08:00 - 17:00 น.') }, { label: t('sundayLabel', 'อาทิตย์'), value: t('closedLabel', 'ปิดทำการ') }] }
  ];

  return (
    <section className="contact-page">
      {/* Header */}
      <div className="contact-header">
        <h1>{t('contactUsTitle', 'ติดต่อเรา')}</h1>
        <p className="contact-tagline">{t('contactUsTagline', 'เราพร้อมให้บริการและตอบทุกคำถามของคุณ')}</p>
      </div>

      {/* Main Content */}
      <div className="contact-content">
        {/* Left Side */}
        <div className="contact-info-section">
          <div className="info-intro">
            <h2>{t('contactChannelsTitle', 'ช่องทางติดต่อ')}</h2>
            <p>{t('contactChannelsDesc', 'หากคุณมีคำถามหรือต้องการคำปรึกษาเกี่ยวกับอุปกรณ์การแพทย์ ติดต่อเราได้ทันทีผ่านช่องทางด้านล่าง')}</p>
          </div>
          <div className="contact-cards">
            {contactInfo.map((info, index) => (
              <div key={index} className="contact-info-card">
                <div className="info-icon">{info.icon}</div>
                <div className="info-content">
                  <h3>{info.title}</h3>
                  {info.details.map((detail, idx) => (
                    <div key={idx} className="info-detail">
                      {detail.label && <span className="detail-label">{detail.label}:</span>}
                      {detail.href ? <a href={detail.href} className="detail-value">{detail.value}</a> : <span className="detail-value">{detail.value}</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Social Media */}
          <div className="social-section">
            <h3>{t('followUsOnSocial', 'ติดตามเราบนโซเชียลมีเดีย')}</h3>
            <div className="social-links">
              <a href="https://www.facebook.com/p/ART-Medical-61576144820349/" target="_blank" rel="noopener noreferrer" className="social-btn facebook"><FaFacebook className="social-icon"/> Facebook</a>
              <a href="https://line.me/ti/p/~@730aicdq" target="_blank" rel="noopener noreferrer" className="social-btn line"><FaLine className="social-icon"/> Line</a>
            </div>
          </div>
        </div>

        {/* Right Side - Contact Form */}
        <div className="contact-form-section">
          {submitted ? (
            <div className="form-success">
              <FaCheckCircle className="success-icon"/>
              <h3>{t('messageSentSuccess', 'ส่งข้อความสำเร็จ!')}</h3>
              <p>{t('messageSentThanks', 'ขอบคุณที่ติดต่อเรา เราจะตอบกลับโดยเร็วที่สุด')}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">{t('formNameLabel', 'ชื่อ - นามสกุล *')}</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder={t('formNamePlaceholder', 'กรอกชื่อของคุณ')} />
              </div>
              <div className="form-group">
                <label htmlFor="phone">{t('formPhoneLabel', 'เบอร์โทรศัพท์ *')}</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder={t('formPhonePlaceholder', '099-888-7777')} />
              </div>
             
              <div className="form-group">
                <label htmlFor="email">{t('formEmailLabel', 'อีเมล *')}</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder={t('formEmailPlaceholder', 'user123@gmail.com')} />
              </div>
              <div className="form-group">
                <label htmlFor="subject">{t('formSubjectLabel', 'หัวข้อ *')}</label>
                <select name="subject" value={formData.subject} onChange={handleChange} required>
                  <option value="">{t('formSubjectOption1', 'เลือกหัวข้อ')}</option>
                  <option value="general">{t('formSubjectOption1', 'ขอใบเสนอราคา')}</option>
                  <option value="support">{t('formSubjectOption4', 'ขอรับการสนับสนุน')}</option>
                  <option value="product">{t('formSubjectOption2', 'สอบถามเกี่ยวกับสินค้า')}</option>
                  <option value="service">{t('formSubjectOption3', 'สอบถามเกี่ยวกับบริการ')}</option>
                  <option value="other">{t('formSubjectOption5', 'อื่นๆ')}</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="message">{t('formMessageLabel', 'ข้อความ *')}</label>
                <textarea name="message" value={formData.message} onChange={handleChange} required rows="6" placeholder={t('formMessagePlaceholder', 'เขียนข้อความของคุณที่นี่...')}></textarea>
              </div>
              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? t('formSendingBtn', 'กำลังส่ง...') : <><FaPaperPlane /> {t('formSubmitBtn', 'ส่งข้อความ')}</>}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Map */}
      <div className="map-section">
        <h2>{t('mapAndLocationTitle', 'แผนที่และที่ตั้ง')}</h2>
        <div className="map-container">
          <iframe
            title={t('mapTitle', 'บริษัท แอสเซนท์ คอร์ปอเรชั่น จำกัด แผนที่')}
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d944.6014592541595!2d98.9594083696149!3d18.735400868942563!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x30da31004afe6169%3A0xd9e0ad6904e71de2!2sAscent%20Corporation%20co.%2C%20ltd!5e0!3m2!1sth!2sth!4v1775278384870!5m2!1sth!2sth"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>
  );
}